// src/components/dialog/DlgPanelAdd.tsx

import { useEffect, useMemo, useState } from 'react'

import { DashboardId, FolderId, PanelShape, SensorId } from '@/types/domain'
import { KARMA_BasicDialog } from '@/types/karma/dialog'

import {
  DlgPanelFolders,
  DlgPanelPresentationLevel,
  DlgPanelSensors,
  DlgPanelSummary,
} from '..'
import { usePanelApi } from '../../hooks'

type DlgPanelAddProps = KARMA_BasicDialog & {
  data: {
    id: DashboardId
    lastPanelDefaults: any
    nextPosition: number
  }
  onSave: () => void
}

export const DlgPanelAdd = ({
  data,
  onClose,
  onSave,
  open,
}: DlgPanelAddProps) => {
  const { id, lastPanelDefaults, nextPosition } = data

  const { save: savePanel } = usePanelApi()

  const panelShape: PanelShape = useMemo(() => {
    const y: number = new Date().getFullYear()
    const m: number = new Date().getMonth()
    const start_date: Date = new Date(y, m - 1, 1)
    const end_date: Date = new Date(y, m, 0)

    return {
      granularity: 'd',
      graphic_type: lastPanelDefaults?.graphic_type ?? 1,
      indicator: lastPanelDefaults?.indicator ?? 1,
      name: 'Neue Kachel',
      position: nextPosition ?? 0,
      presentationLevel: null,
      start_date,
      end_date,
    }
  }, [lastPanelDefaults, nextPosition])

  const [panelData, setPanelData] = useState(panelShape)

  const [selectedFolders, setSelectedFolders] = useState<FolderId[]>([])
  const [selectedSensors, setSelectedSensors] = useState<SensorId[]>([])

  const [step, setStep] = useState('main')

  const onConfirmSave = async () => {
    const start: Date = new Date(panelData.start_date)
    const end: Date = new Date(panelData.end_date)

    const start_date = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`
    const end_date = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`

    const name = panelData.name
    const position = panelData.position
    const indicator = Number(panelData.indicator)
    const graphic_type = Number(panelData.graphic_type)
    const granularity = panelData.granularity
    const presentation_level = panelData.presentationLevel
    const sensor_ids = Array.from(new Set(selectedSensors))

    const record = {
      dashboard_id: id,
      name,
      position,
      indicator,
      graphic_type,
      start_date,
      end_date,
      granularity,
      presentation_level,
      sensor_ids,
    }

    const ok = await savePanel(record)
    if (ok) onSave?.()
  }

  const resetAndClose = () => {
    setPanelData(panelShape)
    setSelectedFolders([])
    setSelectedSensors([])
    setStep('main')
    onClose()
  }

  useEffect(() => {
    if (open === false) return

    setPanelData(panelShape)
  }, [open, panelShape])

  return (
    <>
      <DlgPanelFolders
        data={{ folders: selectedFolders, panel: panelData, mode: 'add' }}
        onChange={panel => setPanelData(panel)}
        onClose={resetAndClose}
        onGotoNext={() => setStep('sensors')}
        open={open && step === 'main'}
        setSelectedFolders={setSelectedFolders}
      />

      <DlgPanelSensors
        data={{ folders: selectedFolders, sensors: selectedSensors }}
        onChange={sIds => setSelectedSensors(sIds)}
        onClose={resetAndClose}
        onGotoNext={() => setStep('presentation')}
        onGotoPrev={() => setStep('main')}
        open={step === 'sensors'}
      />

      <DlgPanelPresentationLevel
        data={panelData.presentationLevel}
        onChange={presentationLevel =>
          setPanelData(prev => ({ ...prev, presentationLevel }))
        }
        onClose={resetAndClose}
        onGotoNext={() => setStep('summary')}
        onGotoPrev={() => setStep('sensors')}
        open={step === 'presentation'}
      />

      <DlgPanelSummary
        data={{
          folders: selectedFolders,
          sensors: selectedSensors,
          panelId: null,
        }}
        onCancel={resetAndClose}
        onConfirm={onConfirmSave}
        onGotoPrev={() => setStep('presentation')}
        open={step === 'summary'}
      />
    </>
  )
}
